/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { createContext, memo, PropsWithChildren, useContext } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';

import { Box, CircularProgress } from '@mui/material';

import { RootState } from '../store';
import { SystemConfiguration } from '../types/superuser/SystemConfiguration';
import { fetchSystemConfiguration } from '../utils/queryFns';

const SystemConfigContext = createContext<SystemConfiguration | undefined>(undefined);

export const SystemConfigContextProvider = memo<PropsWithChildren>(({ children }) => {
  const auth = useSelector((state: RootState) => state.auth);

  const query = useQuery([fetchSystemConfiguration.key], fetchSystemConfiguration.fn, {
    enabled: auth.isAuthenticated,
  });

  if (query.isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100vh">
        <CircularProgress color="primary" size={60} />
      </Box>
    );
  }

  if (query.isError) {
    console.error(`SystemConfigContext: Error during fetch - ${query.error}`);
  }

  return <SystemConfigContext.Provider value={query.data?.payload}>{children}</SystemConfigContext.Provider>;
});

export function useSystemConfig() {
  const context = useContext(SystemConfigContext);
  if (!context) {
    throw Error('SystemConfigContext is undefined');
  }
  return context;
}
